import { useMutation, UseMutationResult } from '@tanstack/react-query'

import { useGraphQLClient } from '@/hooks/useGraphQLClient'
import { Cart } from '@/types/cart'

const CART_GIFT_CARD_REMOVE_MUTATION = `
  mutation removeCartGiftCard($input: CartGiftCardRemoveInput!) {
    removeCartGiftCard(input: $input) {
      success
      message
      cart {
        id
        displayId
        totalAmountCents
        cartToken
        orderId
        coupons {
          coupon {
            code
          }
          amountCents
        }
        giftCards {
          giftCard {
            displayCode
            id
          }
          amountCents
        }
        orderSummary {
          id
          legs {
            id
            legIndex
            legName
            pickupFeeCents
            travelers {
              id
              name
              email
              phone
              shipments {
                id
                trackingId
                totalPriceCents
                retailShipRate
                paidInsuranceCents
                handlingFeeCents
                fuelSurchargeCents
                clearanceFeeCents
                upgradeDiscount
                shipmentLegName
                wholesaleDiscountCents
                shipDate
                arrivalDate
                packagingId
                additionalInfo
                products {
                  id
                  fullName
                  displayName
                  priceCents
                  type
                  coverageAmountCents
                  productLine {
                    id
                    name
                    icon
                    displayName
                  }
                }
              }
            }
            origin {
              address1
              address2
              city
              state
              zip5
              countryCode
            }
            destination {
              address1
              address2
              city
              state
              zip5
              countryCode
            }
          }
        }
      }
    }
  }
`

export interface CartGiftCardRemoveInput {
  cartToken: string
  id: string
}

export interface CartGiftCardRemoveResult {
  cart: Cart
  message: string
  success: boolean
}

export interface CartGiftCardRemoveResponse {
  removeCartGiftCard: CartGiftCardRemoveResult
}

export const useCartGiftCardRemoveApi = (): UseMutationResult<
  CartGiftCardRemoveResult,
  Error,
  CartGiftCardRemoveInput,
  unknown
> => {
  const client = useGraphQLClient() // Avoid re-creating the client on every mutation

  const removeCartGiftCardApi = async (
    input: CartGiftCardRemoveInput,
  ): Promise<CartGiftCardRemoveResult> => {
    const result = await client.request<CartGiftCardRemoveResponse>(
      CART_GIFT_CARD_REMOVE_MUTATION,
      { input },
    )

    if (!result?.removeCartGiftCard) {
      throw new Error('No cart returned from API')
    }

    return result.removeCartGiftCard
  }

  return useMutation<CartGiftCardRemoveResult, Error, CartGiftCardRemoveInput>({
    mutationFn: removeCartGiftCardApi,
  })
}

export default useCartGiftCardRemoveApi
